import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { STORAGE_URL } from '../../api/axios';
import { FiPlus, FiEdit2, FiTrash2, FiMapPin, FiPackage, FiClock, FiPhone, FiX } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';

const DAYS = ['saturday', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const DAY_LABELS: Record<string, { en: string; ar: string }> = {
  sunday: { en: 'Sunday', ar: 'الأحد' },
  monday: { en: 'Monday', ar: 'الاثنين' },
  tuesday: { en: 'Tuesday', ar: 'الثلاثاء' },
  wednesday: { en: 'Wednesday', ar: 'الأربعاء' },
  thursday: { en: 'Thursday', ar: 'الخميس' },
  friday: { en: 'Friday', ar: 'الجمعة' },
  saturday: { en: 'Saturday', ar: 'السبت' },
};

interface DayHours {
  enabled: boolean;
  from: string;
  to: string;
}

type WorkingHours = Record<string, DayHours>;

interface Branch {
  id: number;
  name: string;
  name_ar: string;
  address: string;
  address_ar: string;
  phone: string;
  working_hours: WorkingHours | null;
  google_maps_url: string;
  is_active: boolean;
  sort_order: number;
}

const defaultHours = (): WorkingHours => {
  const h: any = {};
  DAYS.forEach((d) => { h[d] = { enabled: true, from: '09:00', to: '22:00' }; });
  return h;
};

const emptyForm = {
  name: '', name_ar: '', address: '', address_ar: '',
  phone: '', working_hours: defaultHours(),
  google_maps_url: '', is_active: true,
};

export default function Branches() {
  const { store, lang } = useOutletContext<{ store: any; lang: DashLang }>();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Branch | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Stock modal
  const [stockBranch, setStockBranch] = useState<Branch | null>(null);
  const [stockProducts, setStockProducts] = useState<any[]>([]);
  const [stockSaving, setStockSaving] = useState(false);

  useEffect(() => {
    if (store?.id) loadBranches();
  }, [store?.id]);

  const loadBranches = async () => {
    setLoading(true);
    try { const res = await api.get(`/stores/${store.id}/branches`); setBranches(res.data); }
    finally { setLoading(false); }
  };

  const openModal = (branch?: Branch) => {
    if (branch) {
      setEditing(branch);
      setForm({
        name: branch.name || '',
        name_ar: branch.name_ar || '',
        address: branch.address || '',
        address_ar: branch.address_ar || '',
        phone: branch.phone || '',
        working_hours: branch.working_hours || defaultHours(),
        google_maps_url: branch.google_maps_url || '',
        is_active: branch.is_active ?? true,
      });
    } else {
      setEditing(null);
      setForm({ ...emptyForm, phone: store.phone || '', working_hours: defaultHours() });
    }
    setShowModal(true);
  };

  const updateDayHours = (day: string, field: string, value: any) => {
    setForm((prev) => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [day]: { ...prev.working_hours[day], [field]: value },
      },
    }));
  };

  const applyToAll = (day: string) => {
    const src = form.working_hours[day];
    const updated: any = {};
    DAYS.forEach((d) => {
      updated[d] = { ...form.working_hours[d], from: src.from, to: src.to };
    });
    setForm({ ...form, working_hours: updated });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/stores/${store.id}/branches/${editing.id}`, form);
        toast.success(dt('branch_updated', lang));
      } else {
        await api.post(`/stores/${store.id}/branches`, form);
        toast.success(dt('branch_created', lang));
      }
      setShowModal(false);
      loadBranches();
    } catch (err: any) {
      toast.error(err.response?.data?.message || dt('error_saving_branch', lang));
    }
    setSaving(false);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(dt('delete_branch_confirm', lang))) return;
    try {
      await api.delete(`/stores/${store.id}/branches/${id}`);
      toast.success(dt('branch_deleted', lang));
      loadBranches();
    } catch {
      toast.error(dt('error_deleting_branch', lang));
    }
  };

  const handleToggle = async (branch: Branch) => {
    try {
      await api.put(`/stores/${store.id}/branches/${branch.id}`, { is_active: !branch.is_active });
      setBranches((prev) => prev.map((b) => b.id === branch.id ? { ...b, is_active: !b.is_active } : b));
    } catch {
      toast.error(dt('error_saving_branch', lang));
    }
  };

  // Stock management
  const openStockModal = async (branch: Branch) => {
    setStockBranch(branch);
    try {
      const res = await api.get(`/stores/${store.id}/branches/${branch.id}/products`);
      setStockProducts(res.data);
    } catch {
      toast.error('Error loading products');
    }
  };

  const updateStockField = (productId: number, field: string, value: any) => {
    setStockProducts((prev) =>
      prev.map((p) => p.id === productId ? { ...p, [field]: value } : p)
    );
  };

  const saveStock = async () => {
    if (!stockBranch) return;
    setStockSaving(true);
    try {
      const products = stockProducts.map((p) => ({
        product_id: p.id,
        stock: p.branch_stock !== '' && p.branch_stock !== null ? parseInt(p.branch_stock) : null,
        is_available: p.branch_available ?? true,
      }));
      await api.post(`/stores/${store.id}/branches/${stockBranch.id}/products`, { products });
      toast.success(dt('stock_updated', lang));
      setStockBranch(null);
    } catch {
      toast.error('Error saving stock');
    }
    setStockSaving(false);
  };

  // Get summary of working hours for card display
  const getHoursSummary = (wh: WorkingHours | null): string => {
    if (!wh) return '';
    const enabledDays = DAYS.filter((d) => wh[d]?.enabled);
    if (enabledDays.length === 0) return lang === 'ar' ? 'مغلق' : 'Closed';
    if (enabledDays.length === 7) {
      const allSame = enabledDays.every((d) => wh[d].from === wh[enabledDays[0]].from && wh[d].to === wh[enabledDays[0]].to);
      if (allSame) {
        return `${lang === 'ar' ? 'يومياً' : 'Daily'} ${wh[enabledDays[0]].from} - ${wh[enabledDays[0]].to}`;
      }
    }
    return `${enabledDays.length} ${lang === 'ar' ? 'أيام' : 'days'} · ${wh[enabledDays[0]].from} - ${wh[enabledDays[0]].to}`;
  };

  if (!store) return null;

  return (
    <div>
      <div className="page-header">
        <h1>{dt('branches', lang)}</h1>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <FiPlus /> {dt('add_branch', lang)}
        </button>
      </div>

      {loading ? (
        <div className="empty-state"><p>{dt('loading', lang)}...</p></div>
      ) : branches.length === 0 ? (
        <div className="empty-state">
          <FiMapPin />
          <h3>{dt('no_branches', lang)}</h3>
          <p>{dt('add_first_branch', lang)}</p>
          <button className="btn btn-primary" onClick={() => openModal()} style={{ marginTop: 16 }}>
            <FiPlus /> {dt('add_branch', lang)}
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {branches.map((branch) => (
            <div key={branch.id} className="card" style={{ padding: 20, opacity: branch.is_active ? 1 : 0.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16 }}>
                    {lang === 'ar' ? (branch.name_ar || branch.name) : branch.name}
                  </h3>
                  {branch.name_ar && lang !== 'ar' && (
                    <span style={{ fontSize: 13, color: 'var(--gray-400)' }} dir="rtl">{branch.name_ar}</span>
                  )}
                  {branch.name && lang === 'ar' && branch.name_ar && (
                    <span style={{ fontSize: 13, color: 'var(--gray-400)' }}>{branch.name}</span>
                  )}
                </div>
                <div
                  onClick={() => handleToggle(branch)}
                  style={{
                    width: 36, height: 20, borderRadius: 10,
                    background: branch.is_active ? 'var(--primary, #2563eb)' : 'var(--gray-300)',
                    position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 2,
                    left: branch.is_active ? 18 : 2,
                    transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }} />
                </div>
              </div>

              {(branch.address || branch.address_ar) && (
                <div style={{ display: 'flex', alignItems: 'start', gap: 8, fontSize: 13, color: 'var(--gray-500)', marginBottom: 8 }}>
                  <FiMapPin style={{ marginTop: 2, flexShrink: 0 }} />
                  <span>{lang === 'ar' ? (branch.address_ar || branch.address) : branch.address}</span>
                </div>
              )}

              {branch.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--gray-500)', marginBottom: 8 }}>
                  <FiPhone style={{ flexShrink: 0 }} />
                  <span dir="ltr">{branch.phone}</span>
                </div>
              )}

              {branch.working_hours && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--gray-500)', marginBottom: 12 }}>
                  <FiClock style={{ flexShrink: 0 }} />
                  <span>{getHoursSummary(branch.working_hours)}</span>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--gray-100)', paddingTop: 12 }}>
                <button className="btn btn-sm btn-white" onClick={() => openStockModal(branch)} style={{ flex: 1 }}>
                  <FiPackage /> {dt('stock', lang)}
                </button>
                <button className="btn btn-sm btn-white" onClick={() => openModal(branch)}>
                  <FiEdit2 />
                </button>
                <button className="btn btn-sm btn-white" onClick={() => handleDelete(branch.id)} style={{ color: 'var(--danger)' }}>
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Branch form modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2>{editing ? dt('edit_branch', lang) : dt('add_branch', lang)}</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">{dt('branch_name_en', lang)} *</label>
                  <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">{dt('branch_name_ar', lang)}</label>
                  <input className="form-input" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} dir="rtl" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">{dt('address', lang)} ({lang === 'ar' ? 'إنجليزي' : 'English'})</label>
                  <input className="form-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">{dt('address', lang)} ({lang === 'ar' ? 'عربي' : 'Arabic'})</label>
                  <input className="form-input" value={form.address_ar} onChange={(e) => setForm({ ...form, address_ar: e.target.value })} dir="rtl" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">{dt('phone', lang)}</label>
                  <input className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} dir="ltr" />
                </div>
                <div className="form-group">
                  <label className="form-label">{dt('google_maps_url', lang)}</label>
                  <input className="form-input" value={form.google_maps_url} onChange={(e) => setForm({ ...form, google_maps_url: e.target.value })} dir="ltr" placeholder="https://maps.google.com/..." />
                </div>
              </div>

              {/* Working Hours per day */}
              <div className="form-group">
                <label className="form-label" style={{ marginBottom: 8 }}>
                  <FiClock style={{ marginInlineEnd: 6, verticalAlign: 'middle' }} />
                  {dt('working_hours', lang)}
                </label>
                <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
                  {DAYS.map((day, idx) => {
                    const dh = form.working_hours[day] || { enabled: true, from: '09:00', to: '22:00' };
                    return (
                      <div
                        key={day}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', flexWrap: 'nowrap',
                          borderBottom: idx < 6 ? '1px solid var(--gray-100)' : 'none',
                          background: dh.enabled ? 'white' : 'var(--gray-50)',
                          opacity: dh.enabled ? 1 : 0.5,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={dh.enabled}
                          onChange={(e) => updateDayHours(day, 'enabled', e.target.checked)}
                        />
                        <span style={{ minWidth: 70, fontSize: 13, fontWeight: 500, flexShrink: 0 }}>
                          {DAY_LABELS[day][lang]}
                        </span>
                        <input
                          type="time"
                          className="form-input"
                          value={dh.from}
                          onChange={(e) => updateDayHours(day, 'from', e.target.value)}
                          disabled={!dh.enabled}
                          style={{ width: 140, padding: '4px 8px', fontSize: 13 }}
                        />
                        <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>—</span>
                        <input
                          type="time"
                          className="form-input"
                          value={dh.to}
                          onChange={(e) => updateDayHours(day, 'to', e.target.value)}
                          disabled={!dh.enabled}
                          style={{ width: 140, padding: '4px 8px', fontSize: 13 }}
                        />
                        {idx === 0 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-white"
                            onClick={() => applyToAll(day)}
                            style={{ fontSize: 11, padding: '2px 8px', whiteSpace: 'nowrap' }}
                          >
                            {lang === 'ar' ? 'تطبيق للكل' : 'Apply all'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                  {dt('active', lang)}
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-white" onClick={() => setShowModal(false)}>{dt('cancel', lang)}</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? dt('saving', lang) : (editing ? dt('update', lang) : dt('create', lang))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stock management modal */}
      {stockBranch && (
        <div className="modal-overlay" onClick={() => setStockBranch(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>
                {dt('stock', lang)} - {lang === 'ar' ? (stockBranch.name_ar || stockBranch.name) : stockBranch.name}
              </h2>
              <button onClick={() => setStockBranch(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <FiX size={20} />
              </button>
            </div>

            {stockProducts.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 20 }}>
                {dt('no_products', lang)}
              </p>
            ) : (
              <div className="data-table" style={{ maxHeight: 400, overflowY: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>{dt('name', lang)}</th>
                      <th style={{ width: 100 }}>{dt('stock', lang)}</th>
                      <th style={{ width: 80 }}>{dt('available', lang)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockProducts.map((product: any) => (
                      <tr key={product.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {product.image && (
                              <img
                                src={`${STORAGE_URL}/${product.image}`}
                                alt=""
                                style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover' }}
                              />
                            )}
                            <span style={{ fontSize: 13 }}>
                              {lang === 'ar' ? (product.name_ar || product.name) : product.name}
                            </span>
                          </div>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            className="form-input"
                            value={product.branch_stock ?? ''}
                            onChange={(e) => updateStockField(product.id, 'branch_stock', e.target.value)}
                            placeholder="∞"
                            style={{ width: 80, padding: '4px 8px', fontSize: 13 }}
                          />
                        </td>
                        <td>
                          <div
                            onClick={() => updateStockField(product.id, 'branch_available', !product.branch_available)}
                            style={{
                              width: 36, height: 20, borderRadius: 10,
                              background: product.branch_available !== false ? 'var(--primary, #2563eb)' : 'var(--gray-300)',
                              position: 'relative', transition: 'background 0.2s', cursor: 'pointer'
                            }}
                          >
                            <div style={{
                              width: 16, height: 16, borderRadius: '50%', background: '#fff',
                              position: 'absolute', top: 2,
                              left: product.branch_available !== false ? 18 : 2,
                              transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button className="btn btn-white" onClick={() => setStockBranch(null)}>{dt('cancel', lang)}</button>
              <button className="btn btn-primary" onClick={saveStock} disabled={stockSaving}>
                {stockSaving ? dt('saving', lang) : dt('save_changes', lang)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
